// Procedural Web Audio SFX + menu music. No assets needed.
// All sounds are generated via OscillatorNodes routed through a master gain.

let ctx = null;
let masterGain = null;
let muted = false;
let menuLoopId = null;
let menuChordIdx = 0;

const STORAGE_KEY = "md_muted";

// Read stored mute preference
try {
  muted = localStorage.getItem(STORAGE_KEY) === "1";
} catch (e) {
  /* ignore */
}

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(muted));

export const subscribeMute = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const getCtx = () => {
  if (!ctx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    ctx = new Ctx();
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : 0.5;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
};

export const setMuted = (m) => {
  muted = !!m;
  try { localStorage.setItem(STORAGE_KEY, muted ? "1" : "0"); } catch (e) { /* */ }
  if (masterGain) masterGain.gain.value = muted ? 0 : 0.5;
  notify();
};
export const isMuted = () => muted;
export const toggleMuted = () => setMuted(!muted);

// Helper: schedule a quick envelope blip
const blip = ({ freq = 440, type = "square", attack = 0.005, decay = 0.1, peak = 0.2, slideTo = null, when = 0 }) => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) {
    osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + attack + decay);
  }
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  osc.connect(g).connect(masterGain);
  osc.start(t0);
  osc.stop(t0 + attack + decay + 0.05);
};

// ---------- Public SFX ----------
export const playClick = () => {
  blip({ freq: 700, slideTo: 380, type: "square", attack: 0.003, decay: 0.07, peak: 0.18 });
};

export const playSoftClick = () => {
  blip({ freq: 520, slideTo: 320, type: "triangle", attack: 0.003, decay: 0.06, peak: 0.12 });
};

export const playFlip = () => {
  // Whoosh up
  blip({ freq: 220, slideTo: 880, type: "triangle", attack: 0.01, decay: 0.18, peak: 0.14 });
  // Soft tap on land
  setTimeout(() => blip({ freq: 240, slideTo: 180, type: "sine", attack: 0.002, decay: 0.05, peak: 0.1 }), 180);
};

export const playTick = () => {
  blip({ freq: 1400, type: "square", attack: 0.001, decay: 0.025, peak: 0.07 });
};

export const playTimeUp = () => {
  // Three descending alarm beeps
  [880, 740, 600].forEach((f, i) => {
    blip({ freq: f, type: "square", attack: 0.005, decay: 0.45, peak: 0.35, when: i * 0.22 });
  });
};

export const playPoint = () => {
  // Ascending arpeggio C-E-G-C
  [523, 659, 784, 1047].forEach((f, i) => {
    blip({ freq: f, type: "triangle", attack: 0.005, decay: 0.18, peak: 0.22, when: i * 0.06 });
  });
};

export const playWinner = () => {
  // Fanfare
  const notes = [523, 659, 784, 1047, 1319];
  notes.forEach((f, i) => {
    blip({ freq: f, type: "sawtooth", attack: 0.01, decay: 0.45, peak: 0.28, when: i * 0.12 });
  });
  // High chord at the end
  setTimeout(() => {
    [1047, 1319, 1568].forEach((f) =>
      blip({ freq: f, type: "triangle", attack: 0.015, decay: 0.9, peak: 0.18 })
    );
  }, 700);
};

// ---------- Menu background music ----------
// Game-show I-IV-V-I in C major with arpeggios + bass.
const MENU_CHORDS = [
  { notes: [262, 330, 392], bass: 131 }, // C
  { notes: [349, 440, 523], bass: 175 }, // F
  { notes: [392, 494, 587], bass: 196 }, // G
  { notes: [349, 440, 523], bass: 175 }, // F
];

const playMenuBeat = () => {
  const c = getCtx();
  if (!c || !masterGain) return;
  const t0 = c.currentTime;
  const chord = MENU_CHORDS[menuChordIdx % MENU_CHORDS.length];

  // Arpeggio
  chord.notes.forEach((f, i) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(f, t0 + i * 0.09);
    g.gain.setValueAtTime(0.0001, t0 + i * 0.09);
    g.gain.exponentialRampToValueAtTime(0.04, t0 + i * 0.09 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.09 + 0.32);
    osc.connect(g).connect(masterGain);
    osc.start(t0 + i * 0.09);
    osc.stop(t0 + i * 0.09 + 0.35);
  });

  // Bass note
  const bass = c.createOscillator();
  const bg = c.createGain();
  bass.type = "triangle";
  bass.frequency.setValueAtTime(chord.bass, t0);
  bg.gain.setValueAtTime(0.0001, t0);
  bg.gain.exponentialRampToValueAtTime(0.06, t0 + 0.02);
  bg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
  bass.connect(bg).connect(masterGain);
  bass.start(t0);
  bass.stop(t0 + 0.6);

  // Hi-hat tick on offbeat
  setTimeout(() => {
    blip({ freq: 6000, type: "square", attack: 0.001, decay: 0.03, peak: 0.04 });
  }, 400);

  menuChordIdx++;
};

export const startMenuMusic = () => {
  if (menuLoopId) return;
  if (!getCtx()) return;
  menuChordIdx = 0;
  playMenuBeat();
  menuLoopId = setInterval(playMenuBeat, 850);
};

export const stopMenuMusic = () => {
  if (menuLoopId) {
    clearInterval(menuLoopId);
    menuLoopId = null;
  }
};
